/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { PagePath } from '../../../navigation/navigation.enums';
import { PageHeader } from '@keplerco/core';
import { useAppState } from '../../../overmind';
import { Stepper } from '../../../components/general/stepper/stepper';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { DetailsStepLayout } from './details-step/details-step.layout';
import { useParams } from 'react-router';
import { Step } from '../../../components/general/stepper/step/step';
import { useStepperConnector } from '../../../components/general/stepper/use-stepper-connector';
import { StepIds } from './rating-assessment.models';
import { SubjectsStepLayout } from './subjects-step/subjects-step.layout';
import { AssessorsStepLayout } from './assessors-step/assessors-step.layout';
import { SettingsStepLayout } from './settings-step/settings-step.layout';
import { SkillsStepLayout } from './skills-step/skills-step.layout';
import { SummaryStepLayout } from './summary-step/summary-step.layout';
import { AssessmentRequest, PeerAssessmentResponse } from '../../../overmind/actions';
import { useAppActions } from '../../../overmind';

export function RatingAssessmentPage(): JSX.Element {
  const { assessmentSlug } = useParams();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [assessment, setAssessment] = useState<AssessmentRequest>();
  const [peerAssessment, setPeerAssessment] = useState<PeerAssessmentResponse>();

  const stepperConnector = useStepperConnector();

  useEffect(() => {
    async function getData() {
      if (!assessmentSlug) return;

      setIsLoading(true);
      const assessmentResponse = await actions.getAssessment(assessmentSlug);
      setAssessment(assessmentResponse);
      const peerAssessmentResponse = await actions.getPeerAssessment({ assessmentSlug: assessmentSlug });
      setPeerAssessment(peerAssessmentResponse);
      setIsLoading(false);
    }

    getData();
  }, [companyVariables.slug, assessmentSlug]);

  return (
    <PageLoaderLayer path={PagePath.assessmentManagementCreateRatingAssessment}>
      <div className="wrapper stack" style={{ paddingBottom: 24, gap: 24 }}>
        <PageHeader
          breadcrumbs={
            !assessmentSlug
              ? [
                { name: 'Assessment Management', url: PagePath.assessmentManagementBase },
                { name: 'Create assessment', url: `${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreate.replace(':companySlug', companyVariables.slug!)}` },
                { name: assessment?.name ?? 'Rating assessment', url: location.pathname },
              ]
              : [
                { name: 'Assessment Management', url: PagePath.assessmentManagementBase },
                { name: assessment?.name ?? 'Rating assessment', url: location.pathname },
              ]
          }
          title={assessment?.name ?? 'Rating assessment'}
          subtitle={!assessmentSlug ? 'Create a rating assessment by choosing subjects, assessors and skills to rate on' : 'Edit a rating assessment by choosing subjects, assessors and skills to rate on'}
          divider
        />

        <Stepper {...stepperConnector}>
          <Step key={StepIds.Details} label={StepIds.Details} completed={!!assessment?.slug} required>
            <DetailsStepLayout
              assessmentSlug={assessmentSlug}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              assessment={assessment}
              onNext={assessment => {
                setAssessment(assessment);
                if (!peerAssessment)
                  setPeerAssessment({
                    assignees: [],
                    isAnonymous: true,
                    isAssessmentFeedbackEnabled: false,
                    isSkillsFeedbackEnabled: false,
                    isWeightingEnabled: false,
                  });
                stepperConnector.next();
              }}
            />
          </Step>

          <Step key={StepIds.Subjects} label={StepIds.Subjects} completed={!!peerAssessment?.assignees.length}>
            {!!assessment?.slug && !!peerAssessment && (
              <SubjectsStepLayout
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                assessmentSlug={assessment.slug}
                peerAssessment={peerAssessment}
                onBack={stepperConnector.previous}
                onNext={peerAssessment => {
                  setPeerAssessment(peerAssessment);
                  stepperConnector.next();
                }}
              />
            )}
          </Step>

          <Step key={StepIds.Assessors} label={StepIds.Assessors} completed={!!peerAssessment?.assignees.length && peerAssessment.assignees.every(assignee => !!assignee.reviewers.length)}>
            {!!assessment?.slug && !!peerAssessment && (
              <AssessorsStepLayout
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                assessmentSlug={assessment.slug}
                peerAssessment={peerAssessment}
                onBack={stepperConnector.previous}
                onNext={peerAssessment => {
                  setPeerAssessment(peerAssessment);
                  stepperConnector.next();
                }}
              />
            )}
          </Step>

          <Step key={StepIds.Skills} label={StepIds.Skills} completed={!!peerAssessment?.assignees.length && peerAssessment.assignees.every(assignee => !!assignee.companySkills.length)}>
            {!!assessment?.slug && !!peerAssessment && (
              <SkillsStepLayout
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                assessmentSlug={assessment?.slug ?? ''}
                peerAssessment={peerAssessment}
                onBack={stepperConnector.previous}
                onNext={peerAssessment => {
                  setPeerAssessment(peerAssessment);
                  stepperConnector.next();
                }}
              />
            )}
          </Step>

          <Step key={StepIds.Settings} label={StepIds.Settings} completed={!!assessment && !!peerAssessment}>
            {!!assessment && !!peerAssessment && (
              <SettingsStepLayout
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                assessment={assessment}
                peerAssessment={peerAssessment}
                onBack={stepperConnector.previous}
                onNext={({ assessment, peerAssessment }) => {
                  setAssessment(assessment);
                  setPeerAssessment(peerAssessment);
                  stepperConnector.next();
                }}
              />
            )}
          </Step>

          <Step key={StepIds.Summary} label={StepIds.Summary}>
            {!!assessment && !!peerAssessment && <SummaryStepLayout isLoading={isLoading} setIsLoading={setIsLoading} assessment={assessment!} peerAssessment={peerAssessment} goToStep={stepperConnector.goTo} onBack={stepperConnector.previous} />}
          </Step>
        </Stepper>
      </div>
    </PageLoaderLayer>
  );
}
